import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Card from "components/card/Card";
import { Fragment } from "react";
import { useProjectDetailTable } from "./projectDetailTable/hooks/useProjectDetailTable";
import { createTableColumns } from "./projectDetailTable/helpers/tableColumns";
import { columnMinWidth } from "./projectDetailTable/helpers/documentHelpers";


export default function ProjectDetailTable(props: {
  tableData: any;
  onUpdateDocument: Function;
  isFetching: boolean;
  availableTargetLanguages: string[];
}) {
  const { tableData, onUpdateDocument, isFetching, availableTargetLanguages } = props;
  
  const {
    data,
    sorting,
    setSorting,
    translators,
    managers,
    onChangeDocumentStatus,
    onAssignTranslator,
    onAssignManager,
    onClickSaveDocument,
    onDownloadFiles,
    handleChange,
    onClickView,
    onTargetLanguageChange,
  } = useProjectDetailTable(tableData, onUpdateDocument);

  const columns = createTableColumns(
    availableTargetLanguages,
    translators,
    managers,
    tableData,
    {
      onChangeDocumentStatus,
      onAssignTranslator,
      onAssignManager,
      onClickSaveDocument,
      onDownloadFiles,
      handleChange,
      onClickView,
      onTargetLanguageChange,
    }
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });


  return (
    <Card
      flexDirection="column"
      w="100%"
      p="0px"
      overflowX="auto"
      borderRadius="0"
      sx={{
        ".chakra-menu__menu-button + div": {
          position: "fixed !important",
        },
      }}
    >
      <Box w="100%">
        <Table
          w="100%"
          variant="simple"
          color="gray.500"
          pb="2"
          lineHeight="15px"
          borderRadius="0px"
        >
          <Thead display={{ md: "table-header-group", base: "none" }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index: number) => {
                  return (
                    <Th
                      key={index}
                      colSpan={header.colSpan}
                      p="10px"
                      border={"none"}
                      fontFamily="Inter"
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                      textTransform={"capitalize"}
                      position={
                        index === headerGroup.headers.length - 1
                          ? "sticky"
                          : "unset"
                      }
                      right={
                        index === headerGroup.headers.length - 1 ? "0" : "unset"
                      }
                      bgColor="#F1F1F1"
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        lineHeight="24px"
                        sx={{
                          p: {
                            fontSize: "14px",
                            color: "#000",
                          },
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody
            w="100%"
            display={{ md: "table-row-group", base: "flex" }}
            flexDirection="column"
          >
            {!isFetching &&
              table
                .getRowModel()
                .rows.slice(0, 11)
                .map((row) => {
                  return (
                    <Tr
                      key={row.id}
                      position="relative"
                      display={{ md: "table-row", base: "flex" }}
                      flexDirection="column"
                    >
                      {row.getVisibleCells().map((cell, index: number) => {
                        return (
                          <Td
                            padding="15px 10px"
                            key={cell.id}
                            fontSize={{ sm: "14px" }}
                            minW={{
                              base: `${columnMinWidth[cell.column.id]}px`,
                              md: `${columnMinWidth[cell.column.id]}px`,
                              lg: `${columnMinWidth[cell.column.id]}px`,
                            }}
                            position={
                              index === row.getVisibleCells().length - 1
                                ? "sticky"
                                : "unset"
                            }
                            right={
                              index === row.getVisibleCells().length - 1
                                ? "0"
                                : "unset"
                            }
                            bgGradient={
                              index === row.getVisibleCells().length - 1
                                ? "linear-gradient(90.91deg, rgba(255, 255, 255, 0.8) 0.73%, rgba(255, 255, 255, 0.77) 53.73%, rgba(255, 255, 255, 1) 98.58%);"
                                : "unset"
                            }
                            border="none"
                            width={{
                              md:
                                cell.column.id === "actionCol"
                                  ? "80px"
                                  : "auto",
                              base: "100%",
                            }}
                            borderBottom={{
                              md: "none !important",
                              base:
                                cell.column.id === "actionCol" &&
                                "1px solid #EEE !important",
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}

            {isFetching &&
              [1, 2].map((row) => {
                return (
                  <Fragment key={`skeleton-row-${row}`}>
                    <Tr zIndex="1" position="relative">
                      {columns.map((it: any, colIndex: number) => {
                        return (
                          <Td key={`skeleton-${colIndex}`} padding="20px 10px">
                            <Skeleton
                              height="25px"
                              colorScheme="#F3F5F5"
                              borderRadius="5px"
                            ></Skeleton>
                          </Td>
                        );
                      })}
                    </Tr>
                  </Fragment>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
